import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {Plus, Edit, Trash2, Search, MapPin, Clock, Phone, Mail, Instagram, Eye, EyeOff, ExternalLink} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface Store {
  _id: string
  name: string
  name_en?: string
  slug: string
  address: string
  phone?: string
  hours?: string
  googleMapsUrl?: string
  image?: string
  status: 'active' | 'inactive'
  createdAt: string
  updatedAt: string
}

const StoreManager: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [showForm, setShowForm] = useState(false)
  const [editingStore, setEditingStore] = useState<Store | null>(null)

  useEffect(() => {
    fetchStores()
  }, [])

  const fetchStores = async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.stores.list({
        sort: { createdAt: -1 }
      })
      setStores(response.list || [])
    } catch (error) {
      console.error('Failed to fetch stores:', error)
      toast.error('載入分店失敗')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    
    const storeData = {
      name: formData.get('name') as string,
      name_en: formData.get('name_en') as string,
      slug: formData.get('slug') as string,
      address: formData.get('address') as string,
      hours: formData.get('hours') as string,
      googleMapsUrl: formData.get('googleMapsUrl') as string,
      phone: formData.get('phone') as string,
      image: formData.get('image') as string,
      status: formData.get('status') as 'active' | 'inactive',
      createdAt: editingStore?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    try {
      if (editingStore) {
        await lumi.entities.stores.update(editingStore._id, storeData)
        toast.success('分店更新成功')
      } else {
        await lumi.entities.stores.create(storeData)
        toast.success('分店創建成功')
      }
      
      setShowForm(false)
      setEditingStore(null)
      fetchStores()
    } catch (error) {
      console.error('Store operation failed:', error)
      toast.error(editingStore ? '分店更新失敗' : '分店創建失敗')
    }
  }

  const handleEdit = (store: Store) => {
    setEditingStore(store)
    setShowForm(true)
  }

  const handleDelete = async (storeId: string) => {
    if (!confirm('確定要刪除這個分店嗎？')) return

    try {
      await lumi.entities.stores.delete(storeId)
      toast.success('分店刪除成功')
      fetchStores()
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('分店刪除失敗')
    }
  }

  const toggleStatus = async (store: Store) => {
    const newStatus = store.status === 'active' ? 'inactive' : 'active'
    try {
      await lumi.entities.stores.update(store._id, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      toast.success(`分店已${newStatus === 'active' ? '啟用' : '停用'}`)
      fetchStores()
    } catch (error) {
      console.error('Toggle status failed:', error)
      toast.error('操作失敗')
    }
  }

  // 輔助函數：渲染地址（處理可能的物件格式）
  const renderAddress = (address: any): string => {
    if (typeof address === 'string') return address
    if (address && typeof address === 'object') {
      return `${address.street || ''} ${address.city || ''} ${address.district || ''}`.trim()
    }
    return ''
  }

  // 輔助函數：渲染營業時間（處理可能的物件格式）
  const renderHours = (hours: any): string => {
    if (typeof hours === 'string') return hours
    if (hours && typeof hours === 'object') {
      return Object.entries(hours).map(([day, time]) => `${day}: ${time}`).join(' / ')
    }
    return ''
  }

  // 過濾分店
  const filteredStores = stores.filter(store => {
    const matchesSearch = (store.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ((store.name_en || '').toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesStatus = filterStatus === 'all' || store.status === filterStatus
    
    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-light tracking-wider mb-2">實體門市資訊卡管理</h1>
            <p className="text-gray-400">管理首頁與門市頁面的店面資訊卡</p>
          </div>
          <button
            onClick={() => { setEditingStore(null); setShowForm(true) }}
            className="flex items-center px-6 py-3 bg-gray-400 text-black hover:bg-gray-300 transition-colors duration-200 text-sm font-light tracking-wider"
          >
            <Plus size={16} className="mr-2" />
            新增分店
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8 p-4 bg-gray-950 border border-gray-800">
          <div className="flex items-center space-x-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="搜尋分店..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-gray-500 focus:outline-none"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
          >
            <option value="all">所有狀態</option>
            <option value="active">營業中</option>
            <option value="inactive">停業</option>
          </select>
        </div>

        {/* Stores Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStores.map((store) => (
            <motion.div
              key={store._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-950 border border-gray-800 overflow-hidden group"
            >
              {/* Store Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={store.image || 'https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg'}
                  alt={store.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Status Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 text-xs font-light tracking-wider ${
                    store.status === 'active' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  }`}>
                    {store.status === 'active' ? '營業中' : '停業'}
                  </span>
                </div>
              </div>

              {/* Store Info */}
              <div className="p-4">
                <h3 className="text-lg font-light text-gray-100 tracking-wide mb-3">
                  {store.name}
                </h3>

                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-start text-gray-400">
                    <MapPin size={14} className="mr-2 mt-1 flex-shrink-0" />
                    <span className="line-clamp-2">{store.address}</span>
                  </div>

                  {store.phone && (
                    <div className="flex items-start text-gray-400">
                      <Phone size={14} className="mr-2 mt-1 flex-shrink-0" />
                      <span className="line-clamp-1">{store.phone}</span>
                    </div>
                  )}
                  
                  {store.hours && (
                    <div className="flex items-start text-gray-400">
                      <Clock size={14} className="mr-2 mt-1 flex-shrink-0" />
                      <span className="line-clamp-1">{store.hours}</span>
                    </div>
                  )}
                  
                  {store.googleMapsUrl && (
                    <div className="flex items-center text-gray-400">
                      <ExternalLink size={14} className="mr-2 flex-shrink-0" />
                      <a href={store.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="truncate hover:text-gray-200">
                        Google 地圖
                      </a>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-800">
                  <button
                    onClick={() => toggleStatus(store)}
                    className="p-2 text-gray-400 hover:text-gray-100 transition-colors"
                    title={store.status === 'active' ? '停用分店' : '啟用分店'}
                  >
                    {store.status === 'active' ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(store)}
                      className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                      title="編輯分店"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(store._id)}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      title="刪除分店"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredStores.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">沒有找到符合條件的分店</p>
          </div>
        )}
      </div>

      {/* Store Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-950 border border-gray-800 max-w-4xl w-full my-8">
            <div className="p-6">
              <h2 className="text-xl font-light tracking-wider mb-6">
                {editingStore ? '編輯分店' : '新增分店'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="border border-gray-800 p-4">
                  <h3 className="text-lg font-light text-gray-100 mb-4">門市資訊卡欄位</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">店面名稱（中文）*</label>
                      <input
                        name="name"
                        type="text"
                        defaultValue={editingStore?.name || ''}
                        required
                        placeholder="例：ONGU Studio 信義店"
                        className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">店面名稱（英文）</label>
                      <input
                        name="name_en"
                        type="text"
                        defaultValue={editingStore?.name_en || ''}
                        placeholder="ONGU Studio Xinyi"
                        className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-gray-400 mb-2">URL標識符（slug）*</label>
                    <input
                      name="slug"
                      type="text"
                      defaultValue={editingStore?.slug || ''}
                      required
                      placeholder="例：xinyi（用於網址 /zh/store/xinyi）"
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">僅限小寫英文字母、數字和連字號</p>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-gray-400 mb-2">完整地址 *</label>
                    <input
                      name="address"
                      type="text"
                      defaultValue={renderAddress(editingStore?.address)}
                      required
                      placeholder="例：台北市信義區信義路五段7號"
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-gray-400 mb-2">聯絡電話</label>
                    <input
                      name="phone"
                      type="text"
                      defaultValue={editingStore?.phone || ''}
                      placeholder="例：02-2345-6789"
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-gray-400 mb-2">營業時間</label>
                    <input
                      name="hours"
                      type="text"
                      defaultValue={renderHours(editingStore?.hours)}
                      placeholder="例：週一至週五 10:00-20:00 / 週六日 11:00-21:00"
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-gray-400 mb-2">Google Maps 連結</label>
                    <input
                      name="googleMapsUrl"
                      type="url"
                      defaultValue={editingStore?.googleMapsUrl || ''}
                      placeholder="https://maps.google.com/..."
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">用於資訊卡的地圖導航連結</p>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-gray-400 mb-2">資訊卡圖片</label>
                    <input
                      name="image"
                      type="url"
                      defaultValue={editingStore?.image || ''}
                      placeholder="https://..."
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">顯示在首頁與門市頁面的資訊卡圖片</p>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">狀態 *</label>
                  <select
                    name="status"
                    defaultValue={editingStore?.status || 'active'}
                    required
                    className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                  >
                    <option value="active">營業中</option>
                    <option value="inactive">停業</option>
                  </select>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6">
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingStore(null) }}
                    className="px-6 py-3 border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-gray-100 transition-colors duration-200 text-sm font-light tracking-wider"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gray-400 text-black hover:bg-gray-300 transition-colors duration-200 text-sm font-light tracking-wider"
                  >
                    {editingStore ? '更新分店' : '創建分店'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StoreManager